import React, { useEffect, useRef, Suspense } from 'react';
import { View, Text, ActivityIndicator, Animated, TouchableOpacity, Linking, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

import { useAuth } from '../hooks/useAuth';
import { useAppTheme } from '../contexts/ThemeContext';
import { BusinessProvider } from '../contexts/BusinessContext';
import { startDriveDetection, stopDriveDetection } from '../tasks/driveDetection';
import BiometricLock from '../components/BiometricLock';

// Eagerly loaded — needed immediately at startup
import LoginScreen from '../screens/auth/LoginScreen';
import SignUpScreen from '../screens/auth/SignUpScreen';
import OnboardingScreen from '../screens/auth/OnboardingScreen';
import DashboardScreen from '../screens/dashboard/DashboardScreen';

// Lazy-load everything else — parsed only when first navigated to
function makeLazy(importFn) {
  const LazyComp = React.lazy(importFn);
  return function LazyScreen(props) {
    return (
      <Suspense fallback={<View style={{ flex: 1 }} />}>
        <LazyComp {...props} />
      </Suspense>
    );
  };
}

const QuoteListScreen      = makeLazy(() => import('../screens/quotes/QuoteListScreen'));
const NewQuoteScreen       = makeLazy(() => import('../screens/quotes/NewQuoteScreen'));
const QuoteDetailScreen    = makeLazy(() => import('../screens/quotes/QuoteDetailScreen'));
const QuickQuoteScreen     = makeLazy(() => import('../screens/quotes/QuickQuoteScreen'));
const WorkOrderScreen      = makeLazy(() => import('../screens/quotes/WorkOrderScreen'));
const ClientsScreen        = makeLazy(() => import('../screens/clients/ClientsScreen'));
const JobDetailScreen      = makeLazy(() => import('../screens/clients/JobDetailScreen'));
const NewJobScreen         = makeLazy(() => import('../screens/clients/NewJobScreen'));
const SettingsScreen       = makeLazy(() => import('../screens/settings/SettingsScreen'));
const HelpScreen           = makeLazy(() => import('../screens/settings/HelpScreen'));
const AddBusinessScreen    = makeLazy(() => import('../screens/settings/AddBusinessScreen'));
const InvoicesScreen       = makeLazy(() => import('../screens/invoices/InvoicesScreen'));
const InvoiceDetailScreen  = makeLazy(() => import('../screens/invoices/InvoiceDetailScreen'));
const NewInvoiceScreen     = makeLazy(() => import('../screens/invoices/NewInvoiceScreen'));
const SearchScreen         = makeLazy(() => import('../screens/search/SearchScreen'));
const ToolsScreen          = makeLazy(() => import('../screens/tools/ToolsScreen'));
const TemplatesScreen      = makeLazy(() => import('../screens/tools/TemplatesScreen'));
const JobBoardScreen       = makeLazy(() => import('../screens/tools/JobBoardScreen'));
const ExpensesScreen       = makeLazy(() => import('../screens/tools/ExpensesScreen'));
const MaterialsScreen      = makeLazy(() => import('../screens/tools/MaterialsScreen'));
const RoomScannerScreen    = makeLazy(() => import('../screens/tools/RoomScannerScreen'));
const DriveLogScreen       = makeLazy(() => import('../screens/tools/DriveLogScreen'));

const Stack = createNativeStackNavigator();
const Tab = createBottomTabNavigator();
const QuoteStack = createNativeStackNavigator();
const InvoiceStack = createNativeStackNavigator();
const ClientsStack = createNativeStackNavigator();
const ToolsStack = createNativeStackNavigator();
const SettingsStack = createNativeStackNavigator();

const ICON_NAMES = {
  Home:     'home',
  Quotes:   'file-text',
  Invoices: 'clipboard',
  Clients:  'users',
  Tools:    'tool',
  Settings: 'settings',
};

function TabIcon({ label, focused }) {
  const { colors } = useAppTheme();
  const scale = useRef(new Animated.Value(1)).current;
  const glowOpacity = useRef(new Animated.Value(focused ? 1 : 0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: focused ? 1.1 : 1,
        useNativeDriver: true,
        tension: 220,
        friction: 10,
      }),
      Animated.timing(glowOpacity, {
        toValue: focused ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();
  }, [focused]);

  const color = focused ? colors.accent : colors.textMuted;

  return (
    <View style={{ alignItems: 'center', justifyContent: 'center', width: 38, height: 38 }}>
      <Animated.View
        style={{
          position: 'absolute',
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: colors.accentLight,
          opacity: glowOpacity,
          transform: [{ scale }],
        }}
        pointerEvents="none"
      />
      <Animated.View style={{ transform: [{ scale }], zIndex: 1 }}>
        <Feather name={ICON_NAMES[label] || 'circle'} size={20} color={color} />
      </Animated.View>
    </View>
  );
}

function CustomTabBar({ state, descriptors, navigation }) {
  const { colors, radius, isDark } = useAppTheme();
  const insets = useSafeAreaInsets();
  const tabBarBg = isDark ? 'rgba(12, 16, 32, 0.82)' : 'rgba(248, 250, 255, 0.82)';

  return (
    <View
      style={{
        position: 'absolute',
        bottom: insets.bottom + 12,
        left: 20,
        right: 20,
        height: 68,
        backgroundColor: tabBarBg,
        borderRadius: radius.xl,
        flexDirection: 'row',
        borderWidth: 1,
        borderColor: colors.border,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.25,
        shadowRadius: 28,
        elevation: 24,
        overflow: 'hidden',
      }}
    >
      {state.routes.map((route, index) => {
        const isFocused = state.index === index;

        function onPress() {
          const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });
          if (!isFocused && !event.defaultPrevented) {
            navigation.navigate(route.name);
          }
        }

        const labelColor = isFocused ? colors.accent : colors.textMuted;

        return (
          <TouchableOpacity
            key={route.key}
            onPress={onPress}
            activeOpacity={0.8}
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 6 }}
          >
            <TabIcon label={route.name} focused={isFocused} />
            <Text style={{ fontSize: 10, fontWeight: '600', color: labelColor, marginTop: 1, letterSpacing: 0.1 }}>
              {route.name === 'Home' ? 'Home' : route.name}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function QuotesStackNavigator() {
  return (
    <QuoteStack.Navigator screenOptions={{ headerShown: false }}>
      <QuoteStack.Screen name="QuoteList" component={QuoteListScreen} />
      <QuoteStack.Screen name="QuoteDetail" component={QuoteDetailScreen} />
      <QuoteStack.Screen name="Clients" component={ClientsScreen} />
      <QuoteStack.Screen name="NewQuote" component={NewQuoteScreen} options={{ presentation: 'modal' }} />
      <QuoteStack.Screen name="QuickQuote" component={QuickQuoteScreen} options={{ presentation: 'modal' }} />
      <QuoteStack.Screen name="WorkOrder" component={WorkOrderScreen} />
    </QuoteStack.Navigator>
  );
}

function InvoicesStackNavigator() {
  return (
    <InvoiceStack.Navigator screenOptions={{ headerShown: false }}>
      <InvoiceStack.Screen name="InvoiceList" component={InvoicesScreen} />
      <InvoiceStack.Screen name="InvoiceDetail" component={InvoiceDetailScreen} />
      <InvoiceStack.Screen name="NewInvoice" component={NewInvoiceScreen} options={{ presentation: 'modal' }} />
    </InvoiceStack.Navigator>
  );
}

function ClientsStackNavigator() {
  return (
    <ClientsStack.Navigator screenOptions={{ headerShown: false }}>
      <ClientsStack.Screen name="ClientsHome" component={ClientsScreen} />
      <ClientsStack.Screen name="JobDetail" component={JobDetailScreen} />
      <ClientsStack.Screen name="NewJob" component={NewJobScreen} options={{ presentation: 'modal' }} />
    </ClientsStack.Navigator>
  );
}

function ToolsStackNavigator() {
  return (
    <ToolsStack.Navigator screenOptions={{ headerShown: false }}>
      <ToolsStack.Screen name="ToolsHome" component={ToolsScreen} />
      <ToolsStack.Screen name="Templates" component={TemplatesScreen} />
      <ToolsStack.Screen name="JobBoard" component={JobBoardScreen} />
      <ToolsStack.Screen name="Expenses" component={ExpensesScreen} />
      <ToolsStack.Screen name="Materials" component={MaterialsScreen} />
      <ToolsStack.Screen name="RoomScanner" component={RoomScannerScreen} />
      <ToolsStack.Screen name="DriveLog" component={DriveLogScreen} />
    </ToolsStack.Navigator>
  );
}

function SettingsStackNavigator() {
  return (
    <SettingsStack.Navigator screenOptions={{ headerShown: false }}>
      <SettingsStack.Screen name="SettingsMain" component={SettingsScreen} />
      <SettingsStack.Screen name="Help" component={HelpScreen} />
    </SettingsStack.Navigator>
  );
}

const RootStack = createNativeStackNavigator();

function AppTabs() {
  return (
    <Tab.Navigator
      tabBar={(props) => <CustomTabBar {...props} />}
      screenOptions={{ headerShown: false }}
    >
      <Tab.Screen name="Home" component={DashboardScreen} />
      <Tab.Screen name="Quotes" component={QuotesStackNavigator} />
      <Tab.Screen name="Invoices" component={InvoicesStackNavigator} />
      <Tab.Screen name="Clients" component={ClientsStackNavigator} />
      <Tab.Screen name="Tools" component={ToolsStackNavigator} />
      <Tab.Screen name="Settings" component={SettingsStackNavigator} />
    </Tab.Navigator>
  );
}

function AuthStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Login" component={LoginScreen} />
      <Stack.Screen name="SignUp" component={SignUpScreen} />
      <Stack.Screen name="Onboarding" component={OnboardingScreen} />
    </Stack.Navigator>
  );
}

function LoadingScreen() {
  const { colors, typography } = useAppTheme();
  return (
    <View style={{ flex: 1, backgroundColor: colors.background, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ fontSize: typography.sizes.xxl, fontWeight: '700', color: colors.text, letterSpacing: -1 }}>Slate</Text>
      <ActivityIndicator size="small" color={colors.accent} style={{ marginTop: 24 }} />
    </View>
  );
}

async function handleWidgetURL(url, navigationRef) {
  if (!url) return;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname; // e.g. "toggle-drive", "new-quote", "quotes"

    if (host === 'toggle-drive') {
      const action = parsed.searchParams.get('action');
      if (action === 'start') await startDriveDetection();
      else if (action === 'stop') await stopDriveDetection();
    } else if (host === 'new-quote' && navigationRef?.current?.isReady()) {
      navigationRef.current.navigate('AppTabs', {
        screen: 'Quotes',
        params: { screen: 'NewQuote' },
      });
    } else if (host === 'quotes' && navigationRef?.current?.isReady()) {
      navigationRef.current.navigate('AppTabs', { screen: 'Quotes' });
    }
  } catch (_) {}
}

export default function Navigation() {
  const { session, loading, user } = useAuth();
  const navigationRef = useRef(null);

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    // Handle URL that launched the app cold
    Linking.getInitialURL().then((url) => handleWidgetURL(url, navigationRef)).catch(() => {});
    // Handle URL while app is already open / backgrounded
    const sub = Linking.addEventListener('url', ({ url }) => handleWidgetURL(url, navigationRef));
    return () => sub.remove();
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <NavigationContainer ref={navigationRef}>
      {session ? (
        <BusinessProvider userId={user?.id}>
        <BiometricLock>
          <RootStack.Navigator screenOptions={{ headerShown: false }}>
            <RootStack.Screen name="AppTabs" component={AppTabs} />
            <RootStack.Screen name="Search" component={SearchScreen} options={{ presentation: 'modal', animation: 'fade' }} />
            <RootStack.Screen name="HelpModal" component={HelpScreen} options={{ presentation: 'modal' }} />
            <RootStack.Screen name="NewInvoiceModal" component={NewInvoiceScreen} options={{ presentation: 'modal' }} />
            <RootStack.Screen name="AddBusiness" component={AddBusinessScreen} options={{ presentation: 'modal' }} />
          </RootStack.Navigator>
        </BiometricLock>
        </BusinessProvider>
      ) : (
        <AuthStack />
      )}
    </NavigationContainer>
  );
}

